import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Trash2, Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadBlob } from '@/api/client';
import { GmailPagination } from '@/components/ui/GmailPagination';
import { useAuth } from '@/hooks/useAuth';
import { Lead, dateTime } from './leads.api';
import { LeadsSubNav } from './LeadsSubNav';

/**
 * Trash — deleted leads, kept whole.
 *
 * Deleting is soft everywhere in this module, so this screen is the full record: who binned it,
 * when, and the reason they typed. Restore puts it straight back on the pipeline; permanent
 * deletion is Super-Admin-only, because after it there is nothing to restore from.
 */
export function LeadsTrashPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.userType === 'SUPER_ADMIN';
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const params = useMemo(() => ({ page, pageSize, ...(q ? { q } : {}) }), [q, page, pageSize]);

  const { data, isLoading } = useQuery({
    queryKey: ['lead-trash', params],
    queryFn: async () => (await api.get<{ total: number; items: Lead[] }>('/leads/trash', { params })).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['lead-trash'] });
    qc.invalidateQueries({ queryKey: ['leads'] });
    qc.invalidateQueries({ queryKey: ['lead-dashboard'] });
  };

  const restore = useMutation({
    mutationFn: async (id: number) => (await api.post(`/leads/${id}/restore`)).data,
    onSuccess: () => { toast.success('Lead restored'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not restore'),
  });

  const purge = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/leads/${id}/purge`)).data,
    onSuccess: (r: any) => { toast.success(`${r.leadNo} deleted for good`); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not delete'),
  });

  const items = data?.items ?? [];

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Trash</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {(data?.total ?? 0).toLocaleString('en-IN')} deleted lead{(data?.total ?? 0) === 1 ? '' : 's'}. Nothing here counts towards the pipeline or the reports.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2.5 text-gray-400" />
            <input className="border rounded pl-7 pr-2 py-1.5 text-sm dark:bg-slate-900" placeholder="Company / mobile / lead no"
              value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          </div>
          <button onClick={() => downloadBlob(`/leads/export?deleted=1${q ? `&q=${encodeURIComponent(q)}` : ''}`, 'leads-trash.csv')}
            className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm inline-flex items-center gap-1">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      <LeadsSubNav />

      <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-rose-50 dark:bg-slate-800 text-xs uppercase text-rose-900 dark:text-slate-200">
              <tr>
                <th className="text-left px-3 py-2">Lead No</th>
                <th className="text-left px-3 py-2">Company</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">City</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Sale Person</th>
                <th className="text-left px-3 py-2">Deleted</th>
                <th className="text-left px-3 py-2">By</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={10} className="text-center py-8 text-gray-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-8 text-gray-400">Trash is empty.</td></tr>
              ) : items.map((l) => (
                <tr key={l.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800">
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">{l.leadNo}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium line-through decoration-rose-300">{l.companyName}</div>
                    {l.brandName && <div className="text-[11px] text-gray-400">{l.brandName}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>{l.contactName || '—'}</div>
                    <div className="text-gray-400">{l.mobile || l.email || ''}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{l.city || '—'}</td>
                  <td className="px-3 py-2 text-xs">{l.status?.name || '—'}</td>
                  <td className="px-3 py-2 text-xs">{l.ownerName || '—'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{dateTime(l.deletedAt)}</td>
                  <td className="px-3 py-2 text-xs">{l.deletedByName || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 max-w-[220px] truncate" title={l.deleteReason ?? ''}>{l.deleteReason || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button onClick={() => restore.mutate(l.id)}
                        className="px-2 py-1 border rounded text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50 inline-flex items-center gap-1">
                        <RotateCcw size={12} /> Restore
                      </button>
                      {isSuperAdmin && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Permanently delete ${l.companyName}?\n\nThis cannot be undone — its follow-ups and activity history go with it.`)) purge.mutate(l.id);
                          }}
                          className="px-2 py-1 border rounded text-xs text-rose-600 border-rose-200 hover:bg-rose-50 inline-flex items-center gap-1">
                          <Trash2 size={12} /> Delete for good
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>Rows:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border rounded px-1 py-0.5 dark:bg-slate-900">
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <GmailPagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPage={setPage} />
        </div>
      </div>

      {!isSuperAdmin && (
        <p className="text-[11px] text-gray-400 mt-2">
          Only a Super Admin can delete a lead permanently — everyone else can restore it or leave it here.
        </p>
      )}
    </div>
  );
}
